import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, MapPin, ShieldCheck, Star, Cross, Clock, Truck } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { BUSINESS, telHref, whatsappHref, directionsHref } from "@/data/site";

const lines = ["Your Trusted", "Healthcare Partner", "in Jodhpur."];

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineItem = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const panelY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      id="home"
      className="relative overflow-hidden bg-white pt-32 pb-16 lg:pt-40 lg:pb-24"
    >
      {/* soft ambient blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-blue/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-16 h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-green/30 bg-brand-green/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-brand-green-dark"
          >
            <ShieldCheck className="h-4 w-4" />
            100% Genuine Medicines · {BUSINESS.hoursNote}
          </motion.div>

          <motion.h1
            variants={lineWrap}
            initial="hidden"
            animate="show"
            className="mt-6 font-heading font-bold tracking-tighter text-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
          >
            {lines.map((ln, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  variants={lineItem}
                  className={`block ${i === 1 ? "text-brand-blue" : ""}`}
                >
                  {ln}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 max-w-xl text-base lg:text-lg text-slate-600 leading-relaxed"
          >
            Providing genuine medicines, healthcare essentials, and trusted
            pharmacy services with care and reliability.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              data-testid={HERO.callBtn}
              href={telHref}
              className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 transition-all hover:bg-brand-blue-dark hover:-translate-y-0.5 active:scale-95"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <a
              data-testid={HERO.whatsappBtn}
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-green/25 transition-all hover:bg-brand-green-dark hover:-translate-y-0.5 active:scale-95"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Order
            </a>
            <a
              data-testid={HERO.directionsBtn}
              href={directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-semibold text-ink transition-all hover:border-brand-blue hover:text-brand-blue hover:-translate-y-0.5 active:scale-95"
            >
              <MapPin className="h-4 w-4" /> Get Directions
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex items-center gap-6"
          >
            <div className="flex items-center gap-1 text-brand-green">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <p className="text-sm text-slate-500">
              Loved by <span className="font-semibold text-ink">1,000+</span>{" "}
              families across Jodhpur
            </p>
          </motion.div>
        </div>

        {/* Branded name panel */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: panelY }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] bg-brand-blue shadow-[0_30px_60px_rgb(0,75,135,0.25)] px-8 py-14 sm:px-12 sm:py-16">
              {/* decorative */}
              <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-brand-green/25 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
              <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />

              <div className="relative flex flex-col items-center text-center">
                <span className="grid place-items-center h-16 w-16 rounded-3xl bg-brand-green text-white shadow-lg shadow-brand-green/30">
                  <Cross className="h-8 w-8" strokeWidth={2.5} />
                </span>
                <p className="mt-8 font-serif-accent italic text-brand-green-light text-lg">
                  Est. in Shastri Nagar
                </p>
                <h2 className="mt-2 font-heading font-bold tracking-tight text-white text-4xl sm:text-5xl leading-none">
                  PALIWAL
                </h2>
                <p className="mt-2 font-heading text-base font-medium tracking-[0.4em] text-brand-green-light">
                  MEDICOS
                </p>
                <div className="mt-8 h-px w-24 bg-white/20" />
                <p className="mt-6 max-w-xs text-sm text-blue-100/90 leading-relaxed">
                  {BUSINESS.addressShort}
                </p>

                <div className="mt-10 grid grid-cols-3 gap-3 w-full">
                  {[
                    { Icon: ShieldCheck, label: "Genuine" },
                    { Icon: Clock, label: "10AM–11PM" },
                    { Icon: Truck, label: "Quick Delivery" },
                  ].map(({ Icon, label }, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center gap-2 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 py-4 px-2"
                    >
                      <Icon className="h-5 w-5 text-brand-green-light" />
                      <span className="text-[11px] font-medium text-blue-50">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 sm:left-6 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-[0_20px_40px_rgb(0,0,0,0.08)] px-5 py-4"
            >
              <p className="font-heading text-2xl font-bold text-brand-blue">
                13+ hrs
              </p>
              <p className="text-xs text-slate-500">Open daily, {BUSINESS.hours}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
